import store from '@/store/index';
import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action,
  MutationAction,
} from 'vuex-module-decorators';
import { InvoiceParameterModel, InvoiceParameterRequest, InvoiceParameterForm, InvoiceParameterResponse, InvoiceParameterConfig, InvoiceParameterSetting, Snackbar } from './invoice-parameter-types';
import CarrierService from '@/services-http/contract/CarrierService';
import FinancialGroupService from '@/services-http/contract/FinancialGroupService';
import ContractService from '@/services-http/contract/ContractService';
import Functions from '@/shared/functions/functions';
import { defaultFilter, defaultForm, defaultConfig } from './invoice-parameter-factory';
import RuleService from '@/services-http/invoice/RuleService';
import Formatters from '@/shared/formatters/formatters';
import { Carrier, MountEntityConfigsParams, ConfigWithName, toNum, CarrierServiceLike, FinancialGroupServiceLike, ContractServiceLike, unique, buildMap, FinancialGroup, Contract, SubContract, resolveEntityLevelName } from './invoice-parameter-config-types';
import Vue from 'vue';
import rfdc from 'rfdc';

const deepClone = rfdc({ proto: true, circles: true });

const formatter = new Formatters();

@Module({
  namespaced: true,
  name: 'invoice-parameter',
  dynamic: true,
  store,
})
class InvoiceParameterStore extends VuexModule {
  data: InvoiceParameterModel = new InvoiceParameterModel();

  get filter() {
    return this.data.filter;
  }

  get loadingPage() {
    return this.data.loadingPage;
  }

  get loadingEntities() {
    return this.data.loadingEntities;
  }

  get carriers() {
    return this.data.carriers
  }

  get financialGroups() {
    return this.data.financialGroups
  }

  get contracts() {
    return this.data.contracts
  }

  get subContracts() {
    return this.data.subContracts
  }

  get statusList() {
    return [
      { code: true, label: "Ativo" },
      { code: false, label: "Inativo" },
    ]
  }

  get form() {
    return this.data.form;
  }

  get formSelectionStep() {
    const requiredFields = [
      'carrierId', 'financialGroupId',
      'contractId', 'subContractId',
    ];

    const requiredFormFields = {
      ...Object.fromEntries(
        Object.entries(this.data.form)
          .filter(([key]) => requiredFields.includes(key))
      )
    };

    const filledRequiredFormFields = formatter.getFilledKeysFromObject(requiredFormFields);

    return filledRequiredFormFields;
  }

  get currentForm() {
    return this.data.currentForm;
  }

  get formConfigs() {
    return this.data.form.configs;
  }

  get entities() {
    return this.data.entities;
  }

  get entityKeys() {
    return this.data.entityKeys;
  }

  get invoiceParameterStep() {
    return this.data.invoiceParameterStep;
  }

  get configs() {
    return this.data.configs;
  }

  get filterWithFilledFields() {
    return formatter.getFilledKeysFromObject(this.data.filter);
  }

  get hasFiltersSelected() {
    const requiredFields = [
      'carrierId', 'financialGroupId',
      'contractId', 'subContractId',
    ];

    const requiredFilterFields = {
      ...Object.fromEntries(
        Object.entries(this.data.filter)
          .filter(([key]) => requiredFields.includes(key))
      )
    };

    const filledRequiredFilterFields = formatter.getFilledKeysFromObject(requiredFilterFields);

    return Object.keys(filledRequiredFilterFields).length > 0;
  }

  get isCreatingConfig() {
    return this.data.creating;
  }

  get isUpdatingConfig() {
    return this.data.updating;
  }

  get isUpdatingParentConfig() {
    return this.data.form.configs?.some((config) => config.parentRuleConfigId);
  }

  get isCopyingConfig() {
    return this.data.copying;
  }

  get ruleTypes() {
    return this.data.ruleTypes;
  }

  get valueTypes() {
    return this.data.valueTypes;
  }

  get loadingForm() {
    return this.data.loadingForm;
  }

  get formConfigToRequest() {
    const fields = [
      'ruleId', 'carrierId', 'financialGroupId',
      'contractId', 'subContractId', 'configs'
    ];

    let form = {
      ...Object.fromEntries(
        Object.entries(this.data.form)
          .filter(([key]) => fields.includes(key))
      )
    };

    return {...form};
  }

  get showRuleStatusInactiveModal() {
    return this.data.showRuleStatusInactiveModal;
  }

  get showRuleStatusActiveModal() {
    return this.data.showRuleStatusActiveModal;
  }

  get showRuleUpdatedSuccessfullyModal() {
    return this.data.showRuleUpdatedSuccessfullyModal;
  }

  get showConfirmAlterationRuleModal() {
    return this.data.showConfirmAlterationRuleModal;
  }

  get showConflictingRuleFoundModal() {
    return this.data.showConflictingRuleFoundModal;
  }

  get showCreateRuleInactiveModal() {
    return this.data.showCreateRuleInactiveModal
  }

  get showRuleStatusActiveUpdatedModal() {
    return this.data.showRuleStatusActiveUpdatedModal;
  }

  get showRuleStatusInactiveUpdatedModal() {
    return this.data.showRuleStatusInactiveUpdatedModal;
  }

  get showRuleUnlinkModal() {
    return this.data.showRuleUnlinkModal;
  }

  get showRuleLinkModal() {
    return this.data.showRuleLinkModal;
  }

  get showRuleUnlinkSuccessModal() {
    return this.data.showRuleUnlinkSuccessModal;
  }

  get showRuleLinkSuccessModal() {
    return this.data.showRuleLinkSuccessModal;
  }

  get showCancelConfirmModal() {
    return this.data.showCancelConfirmModal;
  }

  get showRelatedRulesModal() {
    return this.data.showRelatedRulesModal;
  }

  get showRuleCreatedModal() {
    return this.data.showRuleCreatedModal;
  }

  get showConfirmInfoModal() {
    return this.data.showConfirmInfoModal;
  }

  get showSnackbarCustomize() {
    return this.data.showSnackbarCustomize;
  }

  get conflictingRuleList() {
    return this.data.conflictingRuleList;
  }

  get configInValidation() {
    return this.data.configInValidation;
  }

  get formsToValidate() {
    return this.data.formsToValidate;
  }

  @Action
  async search() {
    if (!this.hasFiltersSelected) return;
    try {
      this.loading(true);

      const ruleService = new RuleService();

      const paramString = Object.entries(this.filterWithFilledFields)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

      await ruleService.GetInvoiceRuleConfigs(paramString)
        .then(async (response) => {
          let { data } = response;
          const entityIds = await this.mountEntityIds(data);
          const params = {data: data, entityIds: entityIds}
          const configs = await this.mountEntityConfigs(params);
          this.SET_CONFIGS(configs);
        })
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Erro ao buscar os Parâmetros de fatura', show: true}
      this.showSnackbar(snackbarMessage);
    } finally {
      this.loading(false);
    }
  }

  @Action
  async fetchValidationConfigsInLevelSelectionStep() {
    try {
      this.loadingConfig(true);

      const ruleService = new RuleService();

      const paramString = Object.entries(this.formSelectionStep)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

      return await ruleService.GetInvoiceRuleConfigs(paramString)
        .then(async (response) => {
          let { data } = response;
          if (data && Array.isArray(data) && data.length > 0) {
            const entityIds = await this.mountEntityIds(data);
            const params = {data: data, entityIds: entityIds}
            const configs = await this.mountEntityConfigs(params);
            const entityLevelSelected = this.data.form.entityLevel;
            let configsPerEntityLevel: any[] = [];

            switch(entityLevelSelected) {
              case 'CARRIER':
                configsPerEntityLevel = configs.filter(
                  (config) => config.carrierId &&
                              !config.financialGroupId &&
                              !config.contractId &&
                              !config.subContractId
                );
                break;

              case 'FINANCIAL_GROUP':
                configsPerEntityLevel = configs.filter(
                  (config) => config.carrierId &&
                              config.financialGroupId &&
                              !config.contractId &&
                              !config.subContractId
                );
                break;

              case 'CONTRACT':
                configsPerEntityLevel = configs.filter(
                  (config) => config.carrierId &&
                              config.financialGroupId &&
                              config.contractId && parseInt(config.contractId.toString()) === this.data.form.contractId &&
                              !config.subContractId
                );
                break;

              case 'SUB_CONTRACT':
                configsPerEntityLevel = configs.filter(
                  (config) => config.carrierId &&
                              config.financialGroupId &&
                              config.contractId && parseInt(config.contractId.toString()) === this.data.form.contractId &&
                              config.subContractId && parseInt(config.subContractId.toString()) === this.data.form.subContractId
                );
                break;
            }
            this.SET_CONFIGS_IN_CONFLICT(configsPerEntityLevel);
            if (configsPerEntityLevel && configsPerEntityLevel.length > 0) {
              this.setShowConflictingRuleFoundModal(true);
              return false;
            }

            return true;
          }

          return true;
        });
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Erro ao buscar os Parâmetros de fatura para validar', show: true}
      this.showSnackbar(snackbarMessage);
      return false;
    } finally {
      this.loadingConfig(false);
    }
  }

  @Action
  async getRuleData() {
    if (this.data.ruleTypes.length > 0 && this.data.valueTypes.length > 0) return;

    const ruleService = new RuleService();

    try {
      const [
        ruleTypesResponseData,
        valueTypesResponseData
      ] = await Promise.all([
        ruleService.GetInvoiceRuleTypes(),
        ruleService.GetInvoiceValueTypes(),
        this.fetchCarriers(),
        this.fetchFinancialGroups(),
      ]);

      const { data: ruleTypes } = ruleTypesResponseData;
      const { data: valueTypesResponse } = valueTypesResponseData;

      this.SET_RULE_TYPES(ruleTypes);
      this.SET_VALUE_TYPES(Array.isArray(valueTypesResponse.valueTypes) ? valueTypesResponse.valueTypes : []);
    } catch(error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao buscar as regras', show: true}
      this.showSnackbar(snackbarMessage);
    }
  }

  @Action
  async createNewConfigs() {
    const formsToCreate = await this.context.dispatch('mountFormstoCreate', {...this.formConfigToRequest});
    try {
      this.loadingConfig(true);
      for (const form of formsToCreate ?? []) {
          const ruleService = new RuleService();
          await ruleService.PostInvoiceRuleConfig(form);
      }
      return true;
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao criar a nova configuração', show: true}
      this.showSnackbar(snackbarMessage);
      return false;
    } finally {
      this.loadingConfig(false);
    }
  }

  @Action
  async updateFormConfig() {
    const func = new Functions();
    const hasParentConfigIdToUpdate = this.formConfigToRequest.configs.some(
      (config: InvoiceParameterConfig) => config.parentRuleConfigId);

    try {
      this.loadingConfig(true);
      const ruleService = new RuleService();
      let formToRequest = deepClone(this.formConfigToRequest);

      if (!hasParentConfigIdToUpdate) {
        const formToUpdate = await this.context.dispatch('mountFormstoUpdate', formToRequest);
        let approved = true;
        for (let form of formToUpdate ?? []) {
          const isFormToCreate = form.new;
          const isFormToUnlink = form.unlink;
          delete form.new;
          delete form.unlink;
          if (isFormToCreate) {
            await ruleService.PostInvoiceRuleConfig(form);
          }
          if (isFormToUnlink) {
            await ruleService.UnlinkInvoiceRuleConfig(this.data.form.ruleConfigId);
          }
          if (!isFormToCreate && !isFormToUnlink) {
            const updatedConfig = this.data.form.configs?.find((c) => c.tempId === form.tempId);
            const currentConfig = this.data.currentForm.configs?.find((c) => c.tempId === form.tempId);

            const currentFormToValidate = {
              ...this.data.currentForm,
              config: currentConfig
            }
            const updatedFormToValidate = {
              ...this.data.form,
              config: updatedConfig
            }

            if (!func.areEqualDeep(currentConfig, updatedConfig)) {
              this.loadingConfig(false);

              approved = await this.context.dispatch(
                'mountUpdatedFormToValidate',
                {
                  currentForm: currentFormToValidate,
                  updatedForm: updatedFormToValidate
                }
              );

              if (!approved) {
                approved = false;
                break
              };
            }

            this.loadingConfig(true);
            await ruleService.UpdateInvoiceRuleConfig(this.data.form.ruleConfigId, { value: form.value, active: form.active });
          }
        }

        setTimeout(async () => await this.search(), 300);

        return approved;
      }

      const formToUpdate = await this.context.dispatch('mountParentConfigFormstoUpdate', formToRequest);
      let approved = true;
      for (let form of formToUpdate ?? []) {
        const isFormToCreate = form.new;
        const isFormToUnlink = form.unlink;
        delete form.new;
        delete form.unlink;
        if (isFormToCreate) {
          await ruleService.PostInvoiceRuleConfig(form);
        }
        if (isFormToUnlink) {
          await ruleService.UnlinkInvoiceRuleConfig(form.parentRuleConfigId);
        }
        if (!isFormToCreate && !isFormToUnlink) {
          const updatedConfig = this.data.form.configs?.find((c) => c.tempId === form.tempId);
          const currentConfig = this.data.currentForm.configs?.find((c) => c.tempId === form.tempId);
          const currentFormToValidate = {
            ...this.data.currentForm,
            config: currentConfig
          }
          const updatedFormToValidate = {
            ...this.data.form,
            config: updatedConfig
          }

          if (!func.areEqualDeep(currentConfig, updatedConfig)) {
            this.loadingConfig(false);

            approved = await this.context.dispatch(
              'mountUpdatedFormToValidate',
              {
                currentForm: currentFormToValidate,
                updatedForm: updatedFormToValidate
              }
            );

            if (!approved) {
              approved = false;
              break
            };
          }

          this.loadingConfig(true);

          await ruleService.UpdateInvoiceRuleConfig(
            form.parentRuleConfigId, { value: form.value, active: form.active });
        }
      }

      setTimeout(async () => await this.search(), 300);

      return approved;

      return true;
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao atualizar uma configuração', show: true}
      this.showSnackbar(snackbarMessage);
      return false;
    } finally {
      this.loadingConfig(false);
    }
  }

  @Action
  async mountUpdatedFormToValidate(param: {currentForm: any, updatedForm: any}): Promise<boolean> {
    const { currentForm, updatedForm } = param;
    const currentRule = this.ruleTypes.find(r => r.id === currentForm.config.ruleId);
    const updatedRule = this.ruleTypes.find(r => r.id === updatedForm.config.ruleId);
    const formToValidate = [
      { field: 'Tipo de Nível', previous: currentForm.entityLevel, new: updatedForm.entityLevel },
      { field: 'Opção de Nível', previous: currentForm.entityLevelName, new: updatedForm.entityLevelName },
      { field: 'Regra', previous: currentRule?.name, new: updatedRule?.name },
      { field: 'Descrição', previous: currentRule.description, new: updatedRule.description },
      { field: 'Status', previous: currentForm.config.active, new: updatedForm.config.active },
      { field: 'Valor',
        previous: currentForm.config.value,
        new: updatedForm.config.value,
        currentForm: currentForm,
        updatedForm: updatedForm,
      },
    ];

    this.context.commit('setUpdatedFormToValidate', formToValidate);

    return new Promise<boolean>((resolve) => {
      this.context.commit('setPromiseResolve', resolve);
      this.setShowConfirmAlterationRuleModal(true);
    });
  }

  @Action
  async unlinkFormConfig(form: InvoiceParameterResponse) {
    try {
      this.loadingConfig(true);
      const ruleService = new RuleService();
      await ruleService.UnlinkInvoiceRuleConfig(form.ruleConfigId);
      setTimeout(async () => await this.search(), 300);
      return true;
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao desvincular uma configuração', show: true}
      this.showSnackbar(snackbarMessage);
      return false;
    } finally {
      this.loadingConfig(false);
    }
  }

  @Action
  async copyFormConfig() {
    const formsToCopy = await this.context.dispatch('mountFormstoCreate', {...this.formConfigToRequest});
    try {
      this.loadingConfig(true);
      for (const form of formsToCopy ?? []) {
        const ruleService = new RuleService();
        await ruleService.PostInvoiceRuleConfig(form);
        setTimeout(async () => await this.search(), 300);
      }
      return true;
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao copiar uma configuração', show: true}
      this.showSnackbar(snackbarMessage);
      return false;
    } finally {
      this.loadingConfig(false);
    }
  }

  @Action
  private mountFormstoCreate(form: Partial<InvoiceParameterForm>) {
    let formToMount = deepClone(form);
    return formToMount.configs?.map(config => {
      let configValue = null;

      if (!['STRING', 'BOOLEAN'].includes(config.valueType as string)) {
        if (Array.isArray(config.value)) {
          configValue = config.value.join(';');
        }

        if (config.valueType === 'INTEGER') {
          configValue = parseInt(config.value as string, 10);
        }

        if (config.valueType === 'DECIMAL') {
          const floatValue = String(config.value).replace(',', '.');
          configValue = parseFloat(floatValue);
        }
        config.value = configValue;
      }
      return {
        ruleId: config.ruleId,
        value: config.value,
        active: config.active,
        carrierId: form.carrierId,
        financialGroupId: form.financialGroupId,
        contractId: form.contractId,
        subContractId: form.subContractId,
      }
    });
  }

  @Action
  private mountFormstoUpdate(form: Partial<InvoiceParameterForm>) {
    let formToMount = deepClone(form);
    return formToMount.configs?.map(config => {
      let configValue = null;

      if (!['STRING', 'BOOLEAN'].includes(config.valueType as string)) {
        if (Array.isArray(config.value)) {
          configValue = config.value.join(';');
        }

        if (config.valueType === 'INTEGER') {
          configValue = parseInt(config.value as string, 10);
        }

        if (config.valueType === 'DECIMAL') {
          const floatValue = String(config.value).replace(',', '.');
          configValue = parseFloat(floatValue);
        }
        config.value = configValue;
      }
      return {
        tempId: config.tempId,
        ruleId: config.ruleId,
        value: config.value,
        active: config.active,
        carrierId: form.carrierId,
        financialGroupId: form.financialGroupId,
        contractId: form.contractId,
        subContractId: form.subContractId,
        new: config.new,
        unlink: config.unlink,
      }
    });
  }

  @Action
  private mountParentConfigFormstoUpdate(form: InvoiceParameterForm) {
    let formToMount = deepClone(form);
    return formToMount.configs?.map(config => {
      let configValue = null;

      if (!['STRING', 'BOOLEAN'].includes(config.valueType as string)) {
        if (Array.isArray(config.value)) {
          configValue = config.value.join(';');
        }

        if (config.valueType === 'INTEGER') {
          configValue = parseInt(config.value as string, 10);
        }

        if (config.valueType === 'DECIMAL') {
          const floatValue = String(config.value).replace(',', '.');
          configValue = parseFloat(floatValue);
        }
        config.value = configValue;
      }

      return {
        tempId: config.tempId,
        parentRuleConfigId: config.parentRuleConfigId,
        ruleId: config.ruleId,
        value: config.value,
        active: config.active,
        carrierId: form.carrierId,
        financialGroupId: form.financialGroupId,
        contractId: form.contractId,
        subContractId: form.subContractId,
        new: config.new,
        unlink: config.unlink,
      };
    });
  }

  @Action
  private async mountEntityIds(configs: any) {
    const carrierIds = [
      ...new Set(
        configs
          .filter((c: any) => c.entityLevel === 'CARRIER')
          .map((c: any) => parseInt(c.carrierId as string)))] as (string | number)[];
    const financialGroupIds = [
      ...new Set(
        configs
          .filter((c: any) => c.entityLevel === 'FINANCIAL_GROUP')
          .map((c: any) => parseInt(c.financialGroupId as string)))] as (string | number)[];
    const contractIds =  [
      ...new Set(
        configs
          .filter((c: any) => c.entityLevel === 'CONTRACT')
          .map((c: any) => parseInt(c.contractId as string)))] as (string | number)[];
    const subcontractIds = configs
      .filter((c: any) => c.entityLevel === 'SUB_CONTRACT')
      .map((c: any) => (
        {
          contractId: parseInt(c.contractId as string),
          subContractId: parseInt(c.subContractId as string)
        }
      ));

    return {
      carrierIds: carrierIds,
      financialGroupIds: financialGroupIds,
      contractIds: contractIds,
      subContractIds: subcontractIds,
    };
  }

  @Action
  async mountEntityConfigs(params: MountEntityConfigsParams): Promise<ConfigWithName[]> {
    const func = new Functions();
    const { entityIds, data } = params;
    const carrierIds = toNumArray(entityIds.carrierIds);
    const financialGroupIds = toNumArray(entityIds.financialGroupIds);
    const contractIds = toNumArray(entityIds.contractIds);

    const subPairs: Array<{ contractId: number; subcontractId: number }> =
      (entityIds.subContractIds ?? [])
        .map(p => {
          const contractId = toNum(p.contractId);
          const subId = toNum(p.subContractId);
          return contractId !== null && subId !== null ? { contractId, subcontractId: subId } : null;
        })
        .filter((p): p is { contractId: number; subcontractId: number } => p !== null);

    const carrierService: CarrierServiceLike =
      params.services?.carrier ?? new (CarrierService as any)();
    const financialGroupService: FinancialGroupServiceLike =
      params.services?.financialGroup ?? new (FinancialGroupService as any)();
    const contractService: ContractServiceLike =
      params.services?.contract ?? new (ContractService as any)();

    if (!data?.length) return [];

    const [carriersRes, financialGroupsRes, contractsRes] = await Promise.all([
      carrierIds.length
        ? carrierService.FindByIds(carrierIds)
        : Promise.resolve(undefined),
      financialGroupIds.length
        ? financialGroupService.FindByIds(financialGroupIds)
        : Promise.resolve(undefined),
      contractIds.length
        ? contractService.FindByIds(contractIds)
        : Promise.resolve(undefined),
    ]);

    const carriers = carriersRes?.data ?? [];
    const financialGroups = financialGroupsRes?.data ?? [];
    const contracts = contractsRes?.data ?? [];

    const uniqueContractIdsForSubs = unique(subPairs.map(p => p.contractId));
    const subLists = await Promise.all(
      uniqueContractIdsForSubs.map(cid => contractService.FindByParentId(cid))
    );
    const subContracts = subLists.flatMap(r => r?.data ?? []);

    const lookups = {
      carriers: buildMap<Carrier>(carriers),
      financialGroups: buildMap<FinancialGroup>(financialGroups),
      contracts: buildMap<Contract>(contracts),
      subContracts: buildMap<SubContract>(subContracts),
    };

    return data.map<ConfigWithName>(cfg => {
      cfg.name = func.convertText(cfg.name);

      switch(cfg.valueType) {
        case "BOOLEAN":
          if (typeof cfg.value !== 'string') {
            cfg.value = false;
          }

          if (typeof cfg.value === 'string') {
            const normalized = String(cfg.value).trim().toLowerCase();
            cfg.value = normalized === 'true';
          }
          break;
        case "INTEGER":
          cfg.value = parseInt(String(cfg.value));
          break;
        case "DECIMAL":
          cfg.value = parseFloat(String(cfg.value));
          break;
        case "STRING":
          cfg.value = String(cfg.value);
          break;
        default:
          const possibleValues = this.data.valueTypes.find(vt => vt.key === cfg.valueType)?.options ?? [];
          cfg.possibleValues = possibleValues
          cfg.value = String(cfg.value)
            .split(";")
            .map(s => s.trim())
            .filter(Boolean);
      }

      return {
        ...cfg,
        entityLevelName: resolveEntityLevelName(cfg, lookups),
      };
    });
  }

  @Action
  async initPage() {
    this.loading(true);
    await Promise.all([
      this.fetchCarriers(),
      this.fetchFinancialGroups(),
      this.getRuleData()
    ]);
    this.loading(false);
  }

  @Action
  private async fetchCarriers() {
    const carrierService = new CarrierService();

    try {
      if (this.data.carriers.length > 0) return;

      const { data } = await carrierService.FindAll();

      if (data && data.length > 0) {
        this.SET_CARRIERS(data);
      }
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao buscar as Operadoras', show: true}
      this.showSnackbar(snackbarMessage);
    }
  }

  @Action
  private async fetchFinancialGroups() {
    const financialGroupService = new FinancialGroupService();

    try {
      if (this.data.financialGroups.length > 0) return;

      const { data } = await financialGroupService.FindAll();

      if (data && data.length > 0) {
        this.SET_FINANCIAL_GROUPS(data);
      }
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao buscar os Grupos Financeiros', show: true}
      this.showSnackbar(snackbarMessage);
    }
  }

  @Action
  async fetchContractsByFinancialGroupId(isRHProtegido: boolean, returnResponse: boolean) {
    const contract = new ContractService();

    try {
        let query = `financialGroupIds=${this.data.filter.financialGroupId}&onlyMainFields=true`;

        if (isRHProtegido) {
          query += `&allowMovementByProtectedHR=${isRHProtegido}`;
        }
      const { data } = await contract.FindAllByFilters(query);

      const func = new Functions();
      const contractsFormatted = func.formatContractDescription('byFinancialGroupId', data);

      if (contractsFormatted && contractsFormatted.length > 0) {
        if (!returnResponse) {
          this.SET_CONTRACTS(contractsFormatted as []);
        }

        if (returnResponse)
          return contractsFormatted;
      }
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao buscar os Contratos', show: true}
      this.showSnackbar(snackbarMessage);
    }
  }

  @Action
  async fetchSubContractsByContractId(returnResponse: boolean) {
    const contract = new ContractService();

    try {
      const { data } = await contract.FindByParentIdByFilters(this.filter.contractId, '');

      if (data && data.length > 0) {
        if (!returnResponse) {
          this.SET_SUB_CONTRACTS(data);
        }

        if (returnResponse)
          return data;
      }
    } catch (error) {
      const snackbarMessage: Snackbar = {type: 'error', message: 'Ocorreu um erro ao buscar os Subcontratos', show: true}
      this.showSnackbar(snackbarMessage);
    }
  }

  @Action
  resetFilter() {
    this.RESET_FILTER();
  }

  @Action
  applyFilter(filter: Partial<InvoiceParameterRequest>) {
    this.SET_FILTER(filter);
  }

  @Action
  async updateEntityLevel(entityLevel: string) {
    this.LOADING_ENTITIES(true);
    this.SET_ENTITY_LEVEL(entityLevel);

    try {
      switch(entityLevel) {
        case 'CARRIER':
          this.SET_ENTITIES('CARRIER');
          this.SET_ENTITY_KEYS(
            'xipp_commercial_name',
            'id',
          );
          break;

        case 'FINANCIAL_GROUP':
          this.SET_ENTITIES('FINANCIAL_GROUP');
          this.SET_ENTITY_KEYS(
            'name',
            'id',
          );
          break;

        case 'CONTRACT':
          this.SET_ENTITIES('CONTRACT');
          this.SET_ENTITY_KEYS(
            'text',
            'id',
          );
          break;

        case 'SUB_CONTRACT':
          this.SET_ENTITIES('SUB_CONTRACT');
          this.SET_ENTITY_KEYS(
            'text',
            'id',
          );
          break;
      }
    } finally {
      this.LOADING_ENTITIES(false);
    }
  }

  @Action
  private mountConfigForm(form: InvoiceParameterResponse): InvoiceParameterForm {
    const possibleValues = (typeof form.valueType === 'string' && !['STRING', 'INTEGER', 'BOOLEAN', 'DECIMAL'].includes(form.valueType))
      ? this.data.valueTypes.find(vt => vt.key === form.valueType)?.options ?? []
      : [];

    if (form.valueType === 'INTEGER') {
      form.value = String(form.value);
    }

    if (form.valueType === 'DECIMAL') {
      const floatValue = String(form.value).replace('.', ',');
      form.value = floatValue;
    }

    const formConfig = {...form};

    const configs = [{
      tempId: Date.now(),
      ruleId: formConfig.ruleId,
      value: formConfig.value,
      valueType: formConfig.valueType,
      possibleValues: possibleValues,
      description: formConfig.description,
      observation: formConfig.observation,
      active: formConfig.active,
      new: false,
      unlink: false
    }] as InvoiceParameterConfig[];

    return {
      ruleConfigId: form.ruleConfigId,
      carrierId: form.carrierId ? parseInt(String(form.carrierId)) : null,
      financialGroupId: form.financialGroupId ? parseInt(String(form.financialGroupId)) : null,
      contractId: form.contractId ? parseInt(String(form.contractId)) : null,
      subContractId: form.subContractId ? parseInt(String(form.subContractId)) : null,
      name: form.name,
      isApplicable: form.isApplicable,
      entityLevel: form.entityLevel,
      entityLevelName: form.entityLevelName,
      configs: configs,
    };
  }

  @Action
  private mountExistingConfigs(configs: InvoiceParameterResponse[]): InvoiceParameterForm {
    const entityLevelSelected = this.data.form.entityLevel;
    let configsToMount = [...configs];
    let configsWithSameEntityLevel = configsToMount.filter((config) => config.entityLevel === entityLevelSelected);
    let entityConfigs: InvoiceParameterConfig[] = [];
    let counter = 0;
    configsWithSameEntityLevel.forEach((config: InvoiceParameterResponse) => {
      counter++;
      const possibleValues = (typeof config.valueType === 'string' && !['STRING', 'INTEGER', 'BOOLEAN', 'DECIMAL'].includes(config.valueType))
        ? this.data.valueTypes.find(vt => vt.key === config.valueType)?.options ?? []
        : [];

      if (config.valueType === 'INTEGER') {
        config.value = String(config.value);
      }

      if (config.valueType === 'DECIMAL') {
        const floatValue = String(config.value).replace('.', ',');
        config.value = floatValue;
      }

      entityConfigs.push({
        tempId: Date.now() + counter,
        parentRuleConfigId: config.ruleConfigId,
        ruleId: config.ruleId,
        value: config.value,
        valueType: config.valueType,
        possibleValues: possibleValues,
        description: config.description,
        observation: config.observation,
        active: config.active,
        new: false,
        unlink: false
      } as InvoiceParameterConfig);
    });

    return {
      ruleConfigId: null,
      carrierId: configsToMount[0].carrierId ? parseInt(String(configsToMount[0].carrierId)) : null,
      financialGroupId: configsToMount[0].financialGroupId ? parseInt(String(configsToMount[0].financialGroupId)) : null,
      contractId: configsToMount[0].contractId ? parseInt(String(configsToMount[0].contractId)) : null,
      subContractId: configsToMount[0].subContractId ? parseInt(String(configsToMount[0].subContractId)) : null,
      name: configsToMount[0].name,
      isApplicable: configsToMount[0].isApplicable,
      entityLevel: configsToMount[0].entityLevel,
      entityLevelName: configsToMount[0].entityLevelName,
      configs: entityConfigs,
    };
  }

  @Mutation
  setUpdatedFormToValidate(formToValidate: any[]) {
    this.data.formsToValidate = formToValidate;
  }

  @Mutation
  setPromiseResolve(resolve: any) {
    this.data.confirmPromiseResolve = resolve;
  }

  @Mutation
  resolvePromise(resolve: boolean) {
    this.data.confirmPromiseResolve(resolve)
  }

  @Mutation
  goToLevelSelection() {
    this.data.invoiceParameterStep = 1;
  }

  @Mutation
  goToAdvanceRuleDetail() {
    this.data.invoiceParameterStep = 2;
  }

  @Mutation
  setSessionForm(formData: InvoiceParameterForm) {
    this.data.form = formData;
  }

  @Mutation
  setSessionStep(step: number) {
    this.data.invoiceParameterStep = step;
  }

  @Mutation
  setCurrentSessionForm(currentFormData: InvoiceParameterForm) {
    this.data.currentForm = currentFormData;
  }

  @Mutation
  setSessionIsCreating(isCreating: boolean) {
    this.data.creating = isCreating;
  }

  @Mutation
  setSessionIsUpdating(isUpdating: boolean) {
    this.data.updating = isUpdating;
  }

  @Mutation
  setSessionIsCopying(isCopying: boolean) {
    this.data.copying = isCopying;
  }

  @Mutation
  cleanEntities() {
    this.data.form.entityLevel = null;
    this.data.form.entityLevelName = null;
    this.data.form.carrierId = null;
    this.data.form.financialGroupId = null;
    this.data.form.contractId = null;
    this.data.form.subContractId = null;

    this.data.entities = [];
  }

  @Mutation
  setShowRuleStatusInactiveModal(params: {show: boolean, configInValidation: {tempId: number, config: Partial<InvoiceParameterConfig>}}) {
    const { show, configInValidation } = params;
    this.data.showRuleStatusInactiveModal = show;
    if (show) this.data.configInValidation = {...configInValidation};
    if (!show) this.data.configInValidation = null;
  }

  @Mutation
  setShowRuleStatusActiveModal(params: {show: boolean, configInValidation: {tempId: number, config: Partial<InvoiceParameterConfig>}}) {
    const { show, configInValidation } = params;
    this.data.showRuleStatusActiveModal = show;
    if (show) this.data.configInValidation = {...configInValidation};
    if (!show) this.data.configInValidation = null;
  }

  @Mutation
  setShowRuleUpdatedSuccessfullyModal(show: boolean) {
    this.data.showRuleUpdatedSuccessfullyModal = show;
  }

  @Mutation
  setShowConfirmAlterationRuleModal(show: boolean) {
    this.data.showConfirmAlterationRuleModal = show;
  }

  @Mutation
  setShowConflictingRuleFoundModal(show: boolean) {
    this.data.showConflictingRuleFoundModal = show;
  }

  @Mutation
  setShowCreateRuleInactiveModal(show: boolean) {
    this.data.showCreateRuleInactiveModal = show;
  }

  @Mutation
  setShowRuleStatusActiveUpdatedModal(show: boolean) {
    this.data.showRuleStatusActiveUpdatedModal = show;
  }

  @Mutation
  setShowRuleStatusInactiveUpdatedModal(show: boolean) {
    this.data.showRuleStatusInactiveUpdatedModal = show;
  }

  @Mutation
  setShowRuleUnlinkModal(params: {show: boolean, configInValidation: {tempId: number, config: Partial<InvoiceParameterConfig>}}) {
    const { show, configInValidation } = params;
    this.data.showRuleUnlinkModal = show;
    if (show) this.data.configInValidation = {...configInValidation};
    if (!show) this.data.configInValidation = null;
  }

  @Mutation
  setShowRuleLinkModal(params: {show: boolean, configInValidation: {tempId: number, config: Partial<InvoiceParameterConfig>}}) {
    const { show, configInValidation } = params;
    this.data.showRuleLinkModal = show;
    if (show) this.data.configInValidation = {...configInValidation};
    if (!show) this.data.configInValidation = null;
  }

  @Mutation
  setShowRuleUnlinkSuccessModal(show: boolean) {
    this.data.showRuleUnlinkSuccessModal = show;
  }

  @Mutation
  setShowRuleLinkSuccessModal(show: boolean) {
    this.data.showRuleLinkSuccessModal = show;
  }

  @Mutation
  setShowCancelConfirmModal(show: boolean) {
    this.data.showCancelConfirmModal = show;
  }

  @Mutation
  setShowRelatedRulesModal(show: boolean) {
    this.data.showRelatedRulesModal = show;
  }

  @Mutation
  setShowRuleCreatedModal(show: boolean) {
    this.data.showRuleCreatedModal = show;
  }

  @Mutation
  setShowConfirmInfoModal(show: boolean) {
    this.data.showConfirmInfoModal = show;
  }

  @Mutation
  showSnackbar(snackbarParams: Snackbar) {
    this.data.showSnackbarCustomize = snackbarParams;

    setTimeout(() => {
      this.data.showSnackbarCustomize = {
        type: null,
        message: '',
        show: false,
      }
    }, 5000);
  }


  @Mutation
  addConfig() {
    let config = defaultConfig();
    delete config.parentRuleConfigId;
    config.active = true;
    config.new = true;
    if (Array.isArray(this.data.form.configs)) {
      this.data.form.configs.push(config);
    }
  }

  @Mutation
  removeConfig(tempId: number) {
    if (!Array.isArray(this.data.form.configs)) return;
    const configIndex = this.data.form.configs?.findIndex((config) => config.tempId === tempId);
    this.data.form.configs.splice(configIndex, 1);
  }

  @Mutation
  updateSetting(form: Partial<InvoiceParameterForm>) {
    this.data.form = {
      ...this.data.form,
      ...form,
    };
  }

  @Mutation
  updateConfig(payload: { tempId: number; config: Partial<InvoiceParameterConfig> }) {
    let { tempId, config } = payload;
    const configIndex = this.data.form.configs?.findIndex((config) => config.tempId === tempId);

    if (
      Array.isArray(this.data.form.configs) &&
      typeof configIndex === 'number' &&
      configIndex >= 0
    ) {
      Vue.set(this.data.form.configs, configIndex, {
        ...this.data.form.configs[configIndex],
        ...config,
      });
    }
  }

  @Mutation
  startCreateConfig() {
    this.data.form = defaultForm();
    let config = defaultConfig();
    config.new = true;
    config.active = true;
    this.data.form.configs = [config];
    this.data.currentForm = {...this.data.form};
    this.data.invoiceParameterStep = 1;
  }

  @Action
  async startUpdateConfig(form: InvoiceParameterResponse) {
    const formMounted = await this.context.dispatch('mountConfigForm', {...form});
    this.setFormToUpdate(formMounted);
    this.startUpdating();
  }

  @Action
  async startUpdatingExistingConfigs() {
    const formMounted = await this.context.dispatch('mountExistingConfigs', [...this.conflictingRuleList]);
    this.setFormToUpdate(formMounted);
    this.startUpdating();
  }

  @Action
  async startCopyConfig(form: InvoiceParameterResponse) {
    const formMounted = await this.context.dispatch('mountConfigForm', {...form});
    this.startCopyng();
    this.setFormToCopy(formMounted);
  }

  @Mutation
  setFormToUpdate(formMounted: InvoiceParameterForm) {
    this.data.form = { ...formMounted };
    this.data.currentForm = deepClone(this.data.form);
    this.data.invoiceParameterStep = 2;
  }

  @Mutation
  setFormToCopy(form: InvoiceParameterForm) {
    this.data.form = {
      ...form,
      entityLevel: null,
      carrierId: null,
      financialGroupId: null,
      contractId: null,
      subContractId: null,
    };

    this.data.invoiceParameterStep = 1;
  }

  @Mutation
  startCreating() {
    this.data.creating = true;
    this.data.updating = false;
    this.data.copying = false;
  }

  @Mutation
  startUpdating() {
    this.data.creating = false;
    this.data.updating = true;
    this.data.copying = false;
  }


  @Mutation
  startCopyng() {
    this.data.creating = false;
    this.data.updating = false;
    this.data.copying = true;
  }

  @Mutation
  resetCarriers() {
    this.data.carriers = [];
  }

  @Mutation
  resetFinancialGroups() {
    this.data.financialGroups = [];
  }

  @Mutation
  resetContracts() {
    this.data.contracts = [];
  }

  @Mutation
  resetSubContracts() {
    this.data.subContracts = [];
  }

  @Mutation
  resetForm() {
    this.data.form = defaultForm();
    this.data.currentForm = defaultForm();
    this.data.creating = false;
    this.data.updating = false;
    this.data.copying = false;
    this.data.showRuleStatusInactiveModal = false;
    this.data.showRuleStatusActiveModal = false;
    this.data.showRuleUpdatedSuccessfullyModal = false;
    this.data.showConfirmAlterationRuleModal = false;
    this.data.showConflictingRuleFoundModal = false;
    this.data.showCreateRuleInactiveModal = false;
    this.data.showRuleStatusInactiveUpdatedModal = false;
    this.data.showRuleStatusActiveUpdatedModal = false;
    this.data.showCancelConfirmModal = false;
    this.data.showRelatedRulesModal = false;
    this.data.showRuleCreatedModal = false;
    this.data.showConfirmInfoModal = false;
    this.data.showRuleUnlinkModal = false;
    this.data.showRuleLinkModal = false;
    this.data.showRuleUnlinkSuccessModal = false;
    this.data.showRuleLinkSuccessModal = false;
    this.data.conflictingRuleList = [];
    this.data.configInValidation = null;
  }

  @Mutation
  loading(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  loadingConfig(loading: boolean) {
    this.data.loadingForm = loading;
  }

  @Mutation
  private SET_RULE_TYPES(ruleTypes: any[]) {
    const func = new Functions();

    ruleTypes = ruleTypes.map(rt => ({
      ...rt,
      name: func.convertText(rt.name),
    }));

    this.data.ruleTypes = ruleTypes;
  }

  @Mutation
  private SET_VALUE_TYPES(valueTypes: any[]) {
    const func = new Functions();

    valueTypes = valueTypes.map(rt => {
      if (!['STRING', 'INTEGER', 'BOOLEAN', 'DECIMAL'].includes(rt.key)) {
        rt.options = rt.options.map((opt: any) => ({
          ...opt,
          description: func.convertText(opt.description),
        }));
      }
      return rt;
    });

    this.data.valueTypes = valueTypes;
  }

  @Mutation
  private SET_CONFIGS(configs: any[]) {
    this.data.configs = configs;
  }

  @Mutation
  private SET_CONFIGS_IN_CONFLICT(configs: any[]) {
    this.data.conflictingRuleList = configs;
  }

  @Mutation
  private SET_ENTITIES(entityType: string) {
    switch(entityType) {
      case 'carrier':
        this.data.entities = this.data.carriers;
        break;

      case 'financialGroup':
        this.data.entities = this.data.financialGroups;
        break;

      case 'contract':
        this.data.entities = this.data.contracts;
        break;

      case 'subContract':
        this.data.entities = this.data.subContracts;
        break;
    }
  }

  @Mutation
  private SET_ENTITY_KEYS(fieldName: string, fieldValue: string) {
    this.data.entityKeys = {
      itemText: fieldName,
      itemValue: fieldValue,
    }
  }

  @Mutation
  private SET_ENTITY_LEVEL(entityLevel: string) {
    this.data.form.entityLevel = entityLevel;
    this.data.form.carrierId = null;
    this.data.form.financialGroupId = null;
    this.data.form.contractId = null;
    this.data.form.subContractId = null;
  }

  @Mutation
  private RESET_FILTER() {
    this.data.filter = defaultFilter();
    this.data.configs = [];
  }

  @Mutation
  private SET_FILTER(filter: Partial<InvoiceParameterRequest>) {
    this.data.filter = {
      ...this.data.filter,
      ...filter,
    }
  }

  @Mutation
  private SET_CARRIERS(result: []) {
    this.data.carriers = result;
  }

  @Mutation
  private SET_FINANCIAL_GROUPS(result: []) {
    this.data.financialGroups = result;
  }

  @Mutation
  private SET_CONTRACTS(result: []) {
    this.data.contracts = result;
  }

  @Mutation
  private SET_SUB_CONTRACTS(result: []) {
    const func = new Functions();
    const subContractsFormatted = func.formatSubContractDescription(result);

    this.data.subContracts = subContractsFormatted;
  }

  @Mutation
  private LOADING_ENTITIES(loading: boolean) {
    this.data.loadingEntities = loading;
  }
}

export default getModule(InvoiceParameterStore);

function toNumArray(ids: (string | number)[] | undefined): number[] {
  if (!ids) return [];
  return ids.map(toNum).filter((id): id is number => typeof id === 'number' && !isNaN(id));
}
